// ABOUTME: The at-rest product name/price card (SPEC-26 D5's on-screen target) — Figma 45:1659 / 45:2839.
// ABOUTME: Authored on both compositions; each has its own geometry, neither is derived from the other.

import React, { useLayoutEffect, useRef } from 'react';
import { RAIL_UNIT, u } from '../../ReelChrome/railGeometry';
import { MOBILE_UNIT, mu, safeBottom } from '../../ReelChrome/mobileGeometry';
import { PlayerState } from '../useVideoPlayer';
import { useBreakpoint } from '../useViewportSize';
import { useFadePresence, FADE_MS } from './useFadePresence';
import { BAR_W_PCT } from './CaptionOverlay';
import { WAITLIST_PRICE_LABEL } from '../../Waitlist/waitlistGeometry';
import { useControlsLift, useLiftTransition } from '../../../uiExperiment/bottomChromeStore'; // SPEC-82 §4.7

// The only chapter that authors a card. `watch_360` shows the whole piece at rest and names it;
// every other chapter is a feature close-up and uses point-tags instead, and mobile IDLE (45:2600)
// authors no card at all even though it shares watch_360's backdrop. A list rather than an equality
// check so adding a chapter is a data change.
export const PRODUCT_CARD_CHAPTERS = ['watch_360'];

// Desktop — node 369:601 ("Frame 169"), 514.66x262.66 at (602, 949) in the 3840x2160 frame. Still a
// column: the name block over the price. The name's two lines are NOT the same size (55 ExtraBold
// over 42 Medium), which is the opposite of how mobile sets the same content.
//
// ── The 2026-09-04 re-author ────────────────────────────────────────────────
// The card was redrawn with the rest of the desktop frames: it moved from the RIGHT third
// (2770.668, 858) to the left (602, 949), shrank from 431.383x403.702 to 514.66x262.66 (wider and
// much shorter), and every measurement in it came down a step — 75/55.149 type to 55/42, 57.191
// padding to 53.333, 57.191 radius to 37.333, and the gap under the name from 71 to 41.
//
// It is taken WITH the point-tag change rather than left for a card pass because the same
// re-author gave `watch_360` three tags (chapterTagAnchors.js) and one of them, `crystal` at
// (2723, 1029), is drawn straight through where the card used to sit. The two surfaces only clear
// each other at the positions the frame actually sets, and nothing de-clashes a tag against the
// card: the screen-space pass in PointTagLayer only knows about other tags.
// ── The left edge is NOT the frame's 602 (client, live check 2026-09-04) ────
// 369:601 draws the card at x=602 (15.68%) and that read as "too far off the left" on screen. It
// is not a crop artifact — the card positions by viewport percentage, and on anything at or wider
// than 16:9 that IS where the picture puts it — so it is a placement call, and the answer is a
// landmark rather than a nudge: the card's left edge now sits on the CC bar's, which is the exact
// relationship the client asked for on the spec-sheet pill (2026-09-01, "the pill's left edge sits
// on the bottom composition's leftmost column").
//
// Derived from the bar rather than written out, so the two cannot drift apart. The bar is centred
// and 55.69% wide, so its left edge is 22.16% at EVERY viewport width — this alignment holds
// without the card having to track the crop the way a point-tag does.
const DESKTOP_LEFT_PCT = (100 - BAR_W_PCT) / 2;   // 22.16%

const DESKTOP = {
  leftPct: DESKTOP_LEFT_PCT,
  topPct: (949 / 2160) * 100,         // 43.94%
  w: 514.66,
  padX: 53.333,
  padY: 53.333,
  radius: 37.333,
  gap: 41,
  nameW: 408,
  title: 55,
  subtitle: 42,
  price: 42,
  titleLeading: 1.02,
  // The frame sets the SUBTITLE at 1.53 and the price at 1.242 — the extra leading is what opens
  // the authored 18px between the two name lines without a second gap property. Kept apart for
  // that reason; collapsing them to one body leading closes the name block by ~12 authored px.
  subtitleLeading: 1.53,
  bodyLeading: 1.242,
  // 369:604 sets this line `capitalize` — "Black Dial", not "BLACK DIAL". Mobile's 45:2839 runs the
  // name and the descriptor together in ONE uppercase text node, so the two compositions genuinely
  // disagree and the case is per-composition rather than a shared presentation rule.
  subtitleCase: 'capitalize',
  direction: 'column',
};

// Mobile — node 45:2839 ("Frame 164"), 877x207 at (170, 1640) in the 1216x2160 frame. A ROW: the
// name block beside the price, 46 apart, and all three lines at one size (60). Bottom-anchored to
// the same band as the tag row (2160 - (1640 + 207) = 313 vs the row's 311) rather than pinned to
// its authored top, so the card and the row cannot drift apart on a viewport of another shape.
const MOBILE = {
  bottom: 313,
  w: 877,
  padX: 45,
  padY: 45,
  radius: 53.333,
  gap: 46,
  nameW: 460,
  title: 60,
  subtitle: 60,
  price: 60,
  titleLeading: 1.242,
  bodyLeading: 1.242,
  subtitleCase: 'uppercase',
  direction: 'row',
};

// MOBILE ONLY (owner report 2026-09-15): unlike desktop's `useFitName` this card has no shrink-to-
// fit, and until now had no ceiling either ("Mobile keeps its authored auto height", the comment
// this replaces) — a catalogue title long enough to wrap past 2 lines, or a waitlist variant whose
// title+subtitle combine past that, grew the card past what `PointTagLayer.js`'s
// `renderedCardHeight()` assumed when lifting the point-tag row clear of it, so the card's own
// text painted straight over the row instead (reported as "a 4th tag UI that shouldn't be there" —
// screenshot showed 4 wrapped lines on a long waitlist name/variant). 3 lines is the same worst
// case `renderedCardHeight()` now reserves room for; clipping here is what keeps that reservation
// honest instead of both sides silently drifting apart on a long name. No ellipsis: a multi-line
// clamp needs a `-webkit-line-clamp` box, which does not compose with two separate title/subtitle
// `<p>` elements the way it would with one — a clean cut is preferable to the added complexity for
// content this rare.
const MOBILE_NAME_MAX_H = MOBILE.title * MOBILE.titleLeading * 3;

// ── Name auto-fit, desktop only (reported 2026-08-28) ───────────────────────
// 369:601 authors this card around a SHORT model name: "OPH 411 VESPER" over "Black Dial", two
// title lines inside a fixed 514.66x262.66 box. The catalogue's own titles are nearly twice that
// — "411 Vesper Silver - Rhodium" — and at the authored size they wrap FOUR deep: the card stops
// being the authored landscape plate and becomes a tall portrait block, the gap above the
// price reads as a hole, and the widest word ("RHODIUM") spans the full name box, which on
// screen looks like the card has lost its right padding.
//
// So the name keeps its authored vertical allowance and the type shrinks into it — the same
// measure/step treatment SpecSheetOverlay's `useFitToBox` gives a catalogue that outgrows a
// Figma-fixed box. A name that fits (every authored plate, and any short title) measures at 1 and
// renders at exactly the authored sizes, so this is inert on the design's own content.
//
// The allowance is derived from the type above rather than written out, so retuning a token cannot
// leave the box and its contents disagreeing — which is what carried it through the 2026-09-04
// re-author unchanged, at the new sizes.
const DESKTOP_NAME_H =
  DESKTOP.title * DESKTOP.titleLeading * 2 + DESKTOP.subtitle * DESKTOP.subtitleLeading;

// 0.55 matches SpecSheetOverlay's floor: below it the name stops being the loudest thing on the
// card, which is the one piece of hierarchy 369:601 actually has.
const FIT_MIN = 0.55;
const FIT_STEP = 0.05;

/**
 * Authored length × the chrome unit × the auto-fit multiplier. Nests, rather than replaces, `u()`,
 * the same way SpecSheetOverlay's `fit()` does.
 *
 * Exported because it is the only part of this that a unit test can see: jsdom drops any `calc()`
 * carrying a `var()` from `font-size` outright, so the rendered attribute is empty there while a
 * browser resolves it — see the `desktop-product-card-*` plates for the render that proves it.
 */
export const fitLength = (css) => `calc(${css} * var(--fit, 1))`;
const fit = fitLength;

/**
 * Shrink the card's type until the name fits its authored box.
 *
 * Measured on the name block but written to the CARD, so the price and the gap come down with the
 * title: scaling the title alone would leave a 55.149px price shouting over a 52px name, inverting
 * the one hierarchy the plate has. Written straight to the DOM in a measure/step loop rather than
 * as React state — each step changes layout, so a state round-trip per step would paint the
 * intermediate (too-large) sizes. `overflow: hidden` on the name box is what makes `scrollHeight`
 * report the copy's true height. Bails where nothing is laid out (jsdom).
 */
// Layout effects here run before the custom webfont is guaranteed loaded — `font-display: swap`
// paints the fallback first and swaps in Gilroy whenever it arrives, which can change the name's
// wrapped line count without re-running the fit loop below. `resize` was the only re-measure
// trigger; this adds the other one (see SpecSheetOverlay's identical `whenFontsReady` for the fuller
// account, client report 2026-09-02). No-ops outside a real browser.
function whenFontsReady(cb) {
  if (typeof document === 'undefined' || !document.fonts || !document.fonts.ready) return () => {};
  let cancelled = false;
  document.fonts.ready.then(() => {
    if (!cancelled) cb();
  });
  return () => {
    cancelled = true;
  };
}

function useFitName(cardRef, nameRef, signature, enabled) {
  useLayoutEffect(() => {
    const card = cardRef.current;
    const name = nameRef.current;
    if (!card || !name || !enabled) return undefined;

    const measure = () => {
      card.style.setProperty('--fit', '1');
      if (!name.clientHeight) return;             // not laid out — leave it at authored size
      let k = 1;
      while (k > FIT_MIN && name.scrollHeight > name.clientHeight + 1) {
        k = Math.max(FIT_MIN, Math.round((k - FIT_STEP) * 100) / 100);
        card.style.setProperty('--fit', String(k));
      }
    };

    measure();
    const cancelFontWait = whenFontsReady(measure);
    window.addEventListener('resize', measure);
    return () => {
      cancelFontWait();
      window.removeEventListener('resize', measure);
    };
  }, [cardRef, nameRef, signature, enabled]);
}

/**
 * True when the card should be on screen. Same at-rest gate as the point-tag layer (AC-3a): the
 * card belongs to the held picture, so it must not sit over a moving transition or survive a
 * queued departure.
 */
export function shouldShowProductCard({ product, chapterId, playerState, pendingChapterId }) {
  if (!product || pendingChapterId) return false;
  if (!PRODUCT_CARD_CHAPTERS.includes(chapterId)) return false;
  return playerState === PlayerState.HOLDING || playerState === PlayerState.FROZEN;
}

/**
 * @param {{
 *   product: object|null,           // the reel_product envelope (D5)
 *   chapterId: string|null,
 *   playerState: string,
 *   pendingChapterId: string|null,
 * }} props
 */
function ReelProductCard({ product, chapterId, playerState, pendingChapterId }) {
  const composition = useBreakpoint();
  const isMobile = composition === 'mobile';
  const lift = useControlsLift('mobile'); // SPEC-82 control_reveal: 0 unless the row is open
  const liftTransition = useLiftTransition('bottom');
  const box = isMobile ? MOBILE : DESKTOP;
  const unit = isMobile ? MOBILE_UNIT : RAIL_UNIT;
  const px = isMobile ? mu : u;

  const cardRef = useRef(null);
  const nameRef = useRef(null);

  // Straight off the normalized envelope, never a literal (AC-2): `title`, the optional `subtitle`
  // the normalizer joins from the tenant's own subtitle_keys, and the pre-formatted price display.
  // Uppercase is presentation, which is why it is applied here and not expected of the data.
  const liveTitle = product?.title;
  const liveSubtitle = product?.subtitle;
  // Review finding, 2026-09-10: checked EXPLICITLY on the flag, never inferred from the price
  // string being empty — see CollectionOverlay.js's identical fix for why (R6, the legacy
  // `price_info` shim can carry a stale figure even once `waitlist` is set).
  const livePrice = product?.waitlist ? WAITLIST_PRICE_LABEL : (product?.price?.display || product?.price_info?.price);

  // MOBILE ONLY (owner instruction 2026-09-15): the card does not render on mobile at all — mobile
  // holds three point-tags and nothing else in that band; the card is desktop-only chrome. This
  // supersedes the same-day line-clamp fix below (MOBILE_NAME_MAX_H / renderedCardHeight in
  // PointTagLayer.js), which stays in place as a harmless no-op guard rather than being torn back
  // out mid-incident.
  const visible =
    !isMobile
    && shouldShowProductCard({ product, chapterId, playerState, pendingChapterId })
    && !!(liveTitle || livePrice);

  // The card eases in and out rather than popping, like the point tags and the spec sheet — one
  // fade for all the at-rest chrome (client, 2026-09-01). See useFadePresence.
  const { present, opacity } = useFadePresence(visible);

  // What fades OUT is the copy that was last on screen. `visible` drops the instant the envelope
  // clears or the player leaves the hold, and the card is still up for another FADE_MS — reading
  // the live envelope would blank the name mid-fade, or worse, show the NEXT piece's name on the
  // outgoing card. Frozen together so the name, subtitle and price cannot disagree.
  const liveCopy = visible ? { title: liveTitle, subtitle: liveSubtitle, price: livePrice } : null;
  const lastCopyRef = useRef(null);
  if (liveCopy) lastCopyRef.current = liveCopy;
  const copy = liveCopy || lastCopyRef.current;

  // Above the early returns because it is a hook. Mobile is excluded deliberately: 45:2839 is a ROW
  // of three lines at one size, a different box with a different failure mode, and this report is
  // the desktop plate's. Keyed on the LIVE copy so a refit is never triggered by the fade-out.
  //
  // Gated on `present` as well as `visible`, and that pairing is load-bearing rather than belt-and-
  // braces: `useFadePresence` seeds `present` from `useState(visible)` and only raises it later from
  // a passive effect, so on the render where the gate opens this component still returns null and
  // `nameRef` is empty. The layout effect fires against a null ref, bails, and never re-runs —
  // nothing in its dep list changes when the DOM finally appears one render later — so the name
  // never fits. Diagnosed on SpecSheetOverlay (client screenshot, 2026-09-02); the bug is the gate's
  // shape, not the sheet's, and this card wears the same one.
  useFitName(cardRef, nameRef, `${liveTitle}|${liveSubtitle}`, present && visible && !isMobile);

  // `isMobile` here, not folded into `visible` alone: `useFadePresence`'s `present` seeds from
  // `useState(visible)` on FIRST mount only (see the comment above) — a mount where the
  // composition hook's very first render still reads the pre-resize/pre-UA-swap value (`isMobile`
  // false) latches `present` to whatever `visible` was on THAT render and does not revisit it
  // until the fade timer fires, 400ms later. Every other consumer of that gate-timing hazard in
  // this file only ever toggled between two "show" states, so the race was invisible; gating an
  // entire COMPOSITION off it is not, so this re-checks the CURRENT render's `isMobile` directly
  // rather than trusting a `present` that can still be running one render behind it.
  if (!present || !copy || isMobile) return null;
  const { title: rawTitle, subtitle, price } = copy;
  // Owner report 2026-09-15 (corrected): the envelope's `title` repeats the material variant that
  // `subtitle` already carries ("Oph 411 Vesper Silver - Rhodium" / "Silver - Rhodium") — trim
  // that matching tail off the title rather than hiding the subtitle Figma always shows.
  const title = subtitle && rawTitle.toLowerCase().endsWith(subtitle.toLowerCase())
    ? rawTitle.slice(0, rawTitle.length - subtitle.length).replace(/[\s,-]+$/, '')
    : rawTitle;

  // The trailing translateZ(0) on both arms promotes the card onto its own compositor layer — see
  // PointTag.js — without disturbing the mobile centering transform already in the chain.
  const position = isMobile
    ? { bottom: safeBottom(MOBILE.bottom + lift), ...(liftTransition ? { transition: liftTransition } : {}), left: '50%', transform: 'translateX(-50%) translateZ(0)' }
    // Never past the right edge (2026-09-03): the card is left-anchored at 72.15% with a `--u`-floored
    // width, so a desktop-composition viewport narrower than ~640px (a phone with "Request Desktop
    // Site") ran it 77px off-screen, and `maxWidth` cannot rescue a left-anchored box. The `min()`
    // is inert at every width the authored position fits — 768px and up.
    : {
      left: `min(${DESKTOP.leftPct}%, calc(100% - ${px(box.w)} - 2%))`,
      top: `${DESKTOP.topPct}%`,
      transform: 'translateZ(0)',
    };

  return (
    <div
      ref={cardRef}
      data-testid="reel-product-card"
      className="absolute flex items-start bg-reel-chrome-panel backdrop-blur-reel-chrome text-reel-chrome-textPrimary pointer-events-none"
      style={{
        '--u': unit,
        ...position,
        width: px(box.w),
        maxWidth: '92vw',
        flexDirection: box.direction,
        gap: fit(px(box.gap)),
        paddingLeft: px(box.padX),
        paddingRight: px(box.padX),
        paddingTop: px(box.padY),
        paddingBottom: px(box.padY),
        borderRadius: px(box.radius),
        // No `inert` companion to this one, unlike the spec sheet: the card is `pointer-events-none`
        // and carries no controls, so there is nothing to disable while it is on its way out.
        opacity,
        // SPEC-82 review L3: the mobile lift tween rides in `position.transition`; compose it with the fade, never replace it.
        transition: position.transition ? `opacity ${FADE_MS}ms ease, ${position.transition}` : `opacity ${FADE_MS}ms ease`,
        willChange: 'backdrop-filter, transform',
        contain: 'paint',
      }}
    >
      <div
        ref={nameRef}
        className="min-w-0"
        style={{
          maxWidth: px(box.nameW),
          // Desktop: the authored two-line-plus-subtitle allowance, and the clip that lets the fit
          // loop see when the copy has outgrown it. Mobile: no fit loop, but still capped at a
          // generous 3 lines (MOBILE_NAME_MAX_H) so an unusually long title/variant combination
          // clips instead of growing the card past what the tag row lifts clear of (2026-09-15).
          maxHeight: isMobile ? px(MOBILE_NAME_MAX_H) : px(DESKTOP_NAME_H),
          overflow: 'hidden',
        }}
      >
        <p
          className="m-0 font-reel-heading font-bold uppercase"
          style={{
            fontSize: fit(px(box.title)),
            lineHeight: box.titleLeading,
            // Last resort under the fit floor: a single word longer than the 317px name box would
            // otherwise paint straight through the card's right padding rather than wrap.
            overflowWrap: 'break-word',
          }}
        >
          {title}
        </p>
        {/* Only when the envelope carries one. The Figma plate's second line ("BLACK DIAL") is the
            normalizer's `subtitle`, joined from the tenant profile's subtitle_keys — absent for a
            record that configures none, in which case the card is a single name line and a price,
            which is what the design shows for a piece with no variant descriptor. */}
        {subtitle && (
          <p
            className={`m-0 font-reel-medium ${box.subtitleCase}`}
            style={{
              fontSize: fit(px(box.subtitle)),
              // Desktop authors this line at a looser leading than the price (1.53 vs 1.242) —
              // that is where the 18px between the two name lines comes from. Mobile sets one
              // leading for all three, so it falls through to the body value.
              lineHeight: box.subtitleLeading ?? box.bodyLeading,
              overflowWrap: 'break-word',
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {price && (
        <p
          className="m-0 font-reel-medium shrink-0"
          style={{ fontSize: fit(px(box.price)), lineHeight: box.bodyLeading }}
        >
          {price}
        </p>
      )}
    </div>
  );
}

export default ReelProductCard;
export {
  DESKTOP as PRODUCT_CARD_DESKTOP,
  MOBILE as PRODUCT_CARD_MOBILE,
  DESKTOP_NAME_H as PRODUCT_CARD_DESKTOP_NAME_H,
  MOBILE_NAME_MAX_H as PRODUCT_CARD_MOBILE_NAME_MAX_H,
  FIT_MIN as PRODUCT_CARD_FIT_MIN,
};
